import { Phase, IPhaseUpdate } from 'Common/Controls/Dms/MultiMsg/Phase';
import { MultiMsg, Font, DmsConfig } from 'NSwag/ServerClient/Client.nswag';
import { getSignDimensions, ISignDimensions } from 'Common/Controls/Dms/MultiMsg/MultiUtils';
import { lazy } from 'react';

interface IMessageArgs {
    onChange?: (update: IMessageUpdate) => void;
    dmsConfig?: DmsConfig;
    font?: Font | null;
}

export interface IMessageUpdate {
    phases: Phase[],
    multiText: string
}

/**
 * Object representation of the editable message that appears on the DMS
 * consisting of a set of phases.
 */
export class Message {

    onChange?: (update: IMessageUpdate) => void;

    dmsConfig?: DmsConfig;

    signDimensions: ISignDimensions;

    font?: Font | null;

    phases: Phase[];

    constructor({ onChange, dmsConfig, font }: IMessageArgs) {
        this.onChange = onChange;
        this.dmsConfig = dmsConfig;
        this.font = font;
        this.signDimensions = getSignDimensions(this.dmsConfig, this.font);

        let dimensions = getSignDimensions(dmsConfig, font);
        this.phases = [new Phase({
            onChange: this.handlePhaseUpdate,
            signDimensions: this.signDimensions,
            index: 0,
            font: font
        })];
    }

    /**
     * Parse the MultiMsg into phases.
     * @param multiMsg - The MultiMsg to parse.
     */
    parseMultiMsg = (multiMsg: string) => {
        // Remove font and newline tags before splitting since font is
        // already set and multi should not have newlines.
        let phaseStrings = multiMsg.slice()
            .replace(/\[fo[0-9]\]/g, '')
            .replace(/(\r\n|\n|\r)/gm, '')
            .split('[np]')
            .map(str => str.replace(/\[np\]/i, ''));

        this.phases = phaseStrings.map((str, index) =>
            new Phase({
                onChange: this.handlePhaseUpdate,
                signDimensions: this.signDimensions,
                index,
                font: this.font
            }));

        let overflow: string = '';
        this.phases.forEach((phase, index) => {
            let phaseMulti = overflow + phaseStrings[index];
            const phasePrevious = this.phases[index - 1];
            overflow = phase.parsePhaseMulti(phaseMulti, phasePrevious);
        });

        while (overflow.length > 0) {
            let index = this.phases.length;
            this.phases.push(new Phase({
                onChange: this.handlePhaseUpdate,
                signDimensions: this.signDimensions,
                index,
                font: this.font
            }));

            if ((/^\s+$/.test(overflow)) && overflow.charCodeAt(0) === 13) {
                // String is empty but has whitespace that can cause infinite looping.
                break;
            }

            const phasePrevious = this.phases[index - 1];
            overflow = this.phases[index].parsePhaseMulti(overflow, phasePrevious);
        }
    }

    /**
     * Handles changes to a phase.
     * @param update - object containing all the changes to the phase.
     */
    handlePhaseUpdate = (update: IPhaseUpdate) => {
        if (update.overflow && update.overflow.length > 0) {
            this.handlePhaseOverFlow(update);
        } else if (update.dequeuedChars && update.dequeuedChars.length > 0) {
            if (update.phaseIndex > 0) {
                this.phases[update.phaseIndex - 1].appendChars(update.dequeuedChars, true);
                return;
            } else {
                // No previous phase to move chars to, so add back at start of phase.
                this.phases[update.phaseIndex].prependChars(update.dequeuedChars, true);
                return;
            }
        } else if (update.underflow && update.underflow > 0) {
            this.handlePhaseUnderFlow(update);
        } else if (update.setHardPhaseBreak) {
            if (update.phaseIndex === this.phases.length - 1) {
                this.phases.push(new Phase({
                    onChange: this.handlePhaseUpdate,
                    signDimensions: this.signDimensions,
                    index: this.phases.length,
                    font: this.font
                }));
            } else {
                this.phases[update.phaseIndex + 1].prependChars('\r', true);
                return;
            }
        } else if (this.phases[update.phaseIndex].lines.length === 0) {
            this.phases.splice(update.phaseIndex, 1);
        }

        // Update indices in case they changed.
        this.phases.forEach((phase, index) => phase.index = index);
        if (this.onChange) {
            this.onChange({
                phases: this.phases,
                multiText: this.toMultiText()
            });
        }
    }

    /**
     * Handles when a new line overflows.
     * @param update - object containing the phaseIndex and overflow values.
     */
    handlePhaseOverFlow = (update: IPhaseUpdate): void => {
        let index = update.phaseIndex + 1;
        let overflow: string | null = update.overflow!;
        while (overflow) {
            if (!this.phases[index]) {
                this.phases.push(new Phase({
                    onChange: this.handlePhaseUpdate,
                    signDimensions: this.signDimensions,
                    index,
                    font: this.font
                }));
            }

            overflow = this.phases[index].prependChars(overflow);
            index++;
        }
    }

    /**
     * Handles when a line under-flows.
     * @param update - object containing the phaseIndex and under-flow values.
     */
    handlePhaseUnderFlow = (update: IPhaseUpdate): void => {
        let index = update.phaseIndex + 1;
        let underflow: number | null = update.underflow!;
        while (underflow && index < this.phases.length) {
            let chars = this.phases[index].dequeueChars(underflow);
            underflow = underflow === chars.length ? underflow : null;
            this.phases[index - 1].appendChars(chars);
            index++;
        }
    }

    /**
     * Create an empty phase at the specified index.
     * @param index - The index where the new phase is spliced into the existing phases.
     */
    insertPhase = (index: number) => {
        let newPhase = new Phase({
            onChange: this.handlePhaseUpdate,
            signDimensions: this.signDimensions,
            index,
            font: this.font
        });

        this.phases.splice(index, 0, newPhase);
        this.phases.forEach((phase, index) => phase.index = index);

        if (this.onChange) {
            this.onChange({
                phases: this.phases,
                multiText: this.toMultiText()
            });
        }
    }

    /**
     * Remove the phase at the specified index.
     * @param index - The index of the phase to be removed.
     */
    removePhase = (index: number) => {
        this.phases.splice(index, 1);
        this.phases.forEach((phase, index) => phase.index = index);
        if (this.onChange) {
            this.onChange({
                phases: this.phases,
                multiText: this.toMultiText()
            });
        }
    }

    /**
     * Return all the phases joined together as a single line of MultiText.
     */
    toMultiText = (): string => {
        let phaseMulti = this.phases.map(phase => phase.toMultiText());
        return phaseMulti.join('[np]');
    }
}